import { z } from 'zod';
import { SEAT_COUNT } from './seats.js';
import type {
  CardCode,
  PlayerAction,
  SettlementSummary,
  Street,
  TableConfig,
} from './engine/holdem.js';

export const ROOM_CODE_LENGTH = 4;
export const PLAYER_NAME_MAX = 16;

const seatSchema = z.number().int().min(0).max(SEAT_COUNT - 1);

const nameSchema = z
  .string()
  .trim()
  .min(1, '请输入昵称')
  .max(PLAYER_NAME_MAX, `昵称不超过 ${PLAYER_NAME_MAX} 字`);

export const tableConfigSchema = z
  .object({
    smallBlind: z.number().int().min(1).max(10_000),
    bigBlind: z.number().int().min(2).max(20_000),
    startingStack: z.number().int().min(40).max(1_000_000),
  })
  .refine((config) => config.bigBlind > config.smallBlind, {
    message: '大盲必须大于小盲',
    path: ['bigBlind'],
  })
  .refine((config) => config.startingStack >= config.bigBlind * 20, {
    message: '起始筹码至少为大盲的 20 倍',
    path: ['startingStack'],
  });

export const createRoomSchema = z.object({
  name: nameSchema,
  seat: seatSchema.optional(),
  config: tableConfigSchema,
});

export const joinRoomSchema = z.object({
  name: nameSchema,
  roomCode: z.string().transform((value) => normalizeRoomCode(value)),
  seat: seatSchema.optional(),
});

export const resumeSessionSchema = z.object({
  roomCode: z.string().transform((value) => normalizeRoomCode(value)),
  playerToken: z.string().trim().min(8).max(128),
});

export const startHandSchema = z.object({
  roomCode: z.string().transform((value) => normalizeRoomCode(value)),
});

export const actionSchema = z.object({
  roomCode: z.string().transform((value) => normalizeRoomCode(value)),
  action: z.object({
    type: z.enum(['fold', 'check', 'call', 'bet', 'raise', 'all-in']),
    amount: z.number().int().positive().optional(),
  }),
});

export const pingSchema = z.object({
  sentAt: z.number().int().nonnegative(),
});

export type CreateRoomPayload = z.infer<typeof createRoomSchema>;
export type JoinRoomPayload = z.infer<typeof joinRoomSchema>;
export type ResumeSessionPayload = z.infer<typeof resumeSessionSchema>;
export type StartHandPayload = z.infer<typeof startHandSchema>;
export type ActionPayload = z.infer<typeof actionSchema>;
export type PingPayload = z.infer<typeof pingSchema>;

export interface SeatSnapshot {
  seat: number;
  playerId: string;
  name: string;
  stack: number;
  connected: boolean;
  isHost: boolean;
  isDealer: boolean;
  isSmallBlind: boolean;
  isBigBlind: boolean;
  isTurn: boolean;
  folded: boolean;
  allIn: boolean;
  committed: number;
  streetBet: number;
  lastActionLabel: string;
  holeCards: CardCode[];
}

export interface PlayerActionOptions {
  canAct: boolean;
  canFold: boolean;
  canCheck: boolean;
  canCall: boolean;
  callAmount: number;
  minBet: number;
  minRaiseTo: number;
  maxRaiseTo: number;
}

export interface SelfSnapshot {
  playerId: string;
  playerToken: string;
  seat: number;
  holeCards: CardCode[];
  isHost: boolean;
  canStart: boolean;
  actionOptions: PlayerActionOptions;
}

export interface RoomSnapshot {
  roomCode: string;
  blinds: TableConfig;
  handNumber: number;
  street: Street;
  potTotal: number;
  currentBet: number;
  dealerSeat: number | null;
  actionSeat: number | null;
  communityCards: CardCode[];
  seats: Array<SeatSnapshot | null>;
  connectedCount: number;
  canAutoAdvance: boolean;
  lastSettlement: SettlementSummary | null;
  self: SelfSnapshot | null;
}

export interface StandardAck<T = undefined> {
  ok: boolean;
  error?: string;
  roomCode?: string;
  playerId?: string;
  playerToken?: string;
  snapshot?: RoomSnapshot;
  data?: T;
}

export const createAck = <T>(payload: Omit<StandardAck<T>, 'ok'> = {}): StandardAck<T> => ({
  ok: true,
  ...payload,
});

export const errorAck = <T = undefined>(error: string): StandardAck<T> => ({
  ok: false,
  error,
});

export const normalizeRoomCode = (value: string) =>
  value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, ROOM_CODE_LENGTH);

export const isRoomCode = (value: string) => /^[A-Z0-9]{4}$/.test(value);

export const formatActionLabel = (action: PlayerAction | null) => {
  if (!action) {
    return '';
  }

  switch (action.type) {
    case 'fold':
      return '弃牌';
    case 'check':
      return '过牌';
    case 'call':
      return action.amount ? `跟注 ${action.amount}` : '跟注';
    case 'bet':
      return `下注 ${action.amount ?? 0}`;
    case 'raise':
      return `加注 ${action.amount ?? 0}`;
    case 'all-in':
      return '全下';
    default:
      return '';
  }
};
